<script setup lang="ts">
import { computed } from 'vue'
import { ExternalLink } from 'lucide-vue-next'
import type { Citation } from './useResearchChat'
import { extractActualUrl } from './citationUtils'

const props = defineProps<{
  citation: Citation
  index: number
}>()
const emit = defineEmits<{
  select: [citation: Citation]
}>()

const actualUrl = computed(() => {
  return props.citation.url ? extractActualUrl(props.citation.url) : ''
})

const domain = computed(() => {
  if (!actualUrl.value) return ''
  try {
    return new URL(actualUrl.value).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
})

const displayTitle = computed(() => {
  return props.citation.title || domain.value || actualUrl.value || 'Source'
})

// Publisher initials for the favicon tile (e.g. "ft.com" -> "FT").
const initials = computed(() => {
  const base = domain.value.split('.')[0] || displayTitle.value
  return base.slice(0, 2).toUpperCase()
})

// Stable hue derived from the domain so each source gets a consistent tile colour.
const tileColor = computed(() => {
  const seed = domain.value || displayTitle.value
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  const hue = hash % 360
  return `oklch(0.62 0.13 ${hue})`
})

const paddedIndex = computed(() => String(props.index).padStart(2, '0'))

const handleClick = () => {
  if (!actualUrl.value) return
  emit('select', props.citation)
}
</script>

<template>
  <button
    type="button"
    class="border-border/70 group flex items-start gap-3.5 border-t py-3.5 pr-1 text-left first:border-t-0"
    @click="handleClick"
  >
    <span
      class="text-rc-accent font-mont-blanc-regular mt-0.5 w-4 shrink-0 text-[11px] tracking-wide"
    >
      {{ paddedIndex }}
    </span>
    <span
      class="font-mont-blanc-regular mt-px flex size-[22px] shrink-0 items-center justify-center rounded-md text-[11px] font-semibold text-white"
      :style="{ backgroundColor: tileColor }"
    >
      {{ initials }}
    </span>
    <span class="min-w-0 flex-1">
      <span
        class="font-panama-regular text-foreground group-hover:text-rc-accent block text-[15px] leading-snug transition-colors"
      >
        {{ displayTitle }}
      </span>
      <span
        v-if="citation.snippet"
        class="text-muted-foreground mt-1 line-clamp-2 block text-[12.5px] leading-normal"
      >
        {{ citation.snippet }}
      </span>
      <span v-if="domain" class="text-muted-foreground/70 mt-1.5 block text-[11px]">
        {{ domain }}
      </span>
    </span>
    <ExternalLink
      class="text-muted-foreground/70 mt-1 size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
    />
  </button>
</template>
